import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';

const WarningIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className="tw-shrink-0 tw-mt-0.5"
  >
    <circle cx="10" cy="10" r="10" fill="#E03B3B" />
    <rect x="9" y="5" width="2" height="6" rx="1" fill="white" />
    <rect x="9" y="13" width="2" height="2" rx="1" fill="white" />
  </svg>
);

interface Props {
  isQrMode?: boolean;
  isDeeplinkHandoffQr?: boolean;
}

export const SqPaymentDetailsRules: React.FC<Props> = ({
  isQrMode,
  isDeeplinkHandoffQr,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const {
    order,
    currencySymbol,
    requisitesBankName,
    shouldShowQrCode,
    paymentTypeFlags: { isMobileOperator },
  } = useOrderContext();

  const warningText = useMemo(() => {
    if (isMobileOperator) {
      return t('rules_sim.warning');
    }

    // the handoff QR encodes our own page and is scanned with the phone
    // camera, not with a banking app
    if (isDeeplinkHandoffQr) {
      return t('deeplink_qr_description');
    }

    if (shouldShowQrCode || isQrMode) {
      return t('qr_code_description');
    }

    if (requisitesBankName) {
      return t('sq_warning', {
        amount: `${order.amount} ${currencySymbol}`,
        bank: requisitesBankName,
      });
    }

    return t('sq_warning_no_bank', {
      amount: `${order.amount} ${currencySymbol}`,
    });
  }, [
    currencySymbol,
    isQrMode,
    isDeeplinkHandoffQr,
    isMobileOperator,
    order.amount,
    requisitesBankName,
    shouldShowQrCode,
    t,
  ]);

  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-bg-red-200 tw-rounded-3xl tw-px-4 tw-py-3 tw-my-2">
      <WarningIcon />
      <p className="tw-text-sm tw-m-0 tw-leading-6">{warningText}</p>
    </div>
  );
};
